// Submenu — Mobile drawer controller
//
// Bottom sheet with drag-to-dismiss via Pointer Events.
// Closes on drag past 50% height or on a fast flick (800px/s).
//
// Targets:
//   panel   → the draggable drawer element
//   overlay → backdrop that fades with drag progress
//
// Usage:
//   <div data-controller="submenu--drawer">
//     <div class="submenu-drawer" data-submenu--drawer-target="panel"
//       data-action="pointerdown->submenu--drawer#startDrag ...">
//     <div class="submenu-overlay" data-submenu--drawer-target="overlay">

import { Controller } from '@hotwired/stimulus'

const CLOSE_THRESHOLD = 0.5
const VELOCITY_THRESHOLD = 800 // px/s

export default class extends Controller {
  static targets = ['panel', 'overlay']

  dragging = false
  translateY = 0
  frameRequest = null
  nextClientY = null
  velocity = 0

  disconnect() {
    if (this.frameRequest) cancelAnimationFrame(this.frameRequest)
    this.#setWillChange(false)
  }

  close() {
    this.#reset()
    this.dispatch("requestClose", { prefix: "submenu", bubbles: true })
  }

  startDrag(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return

    this.dragging = true
    this.height = this.panelTarget.offsetHeight
    this.lastY = event.clientY
    this.lastTime = event.timeStamp
    this.velocity = 0
    this.#setWillChange(true)

    this.panelTarget.style.transition = 'none'
    if (this.hasOverlayTarget) this.overlayTarget.style.transition = 'none'
    this.panelTarget.setPointerCapture(event.pointerId)
  }

  drag(event) {
    if (!this.dragging) return

    this.nextClientY = event.clientY
    this.nextTime = event.timeStamp
    if (this.frameRequest) return
    this.frameRequest = requestAnimationFrame(() => {
      this.frameRequest = null
      if (this.nextClientY === null) return
      this.#applyDrag(this.nextClientY, this.nextTime)
      this.nextClientY = null
    })
  }

  endDrag(event) {
    if (!this.dragging) return

    if (this.frameRequest) {
      cancelAnimationFrame(this.frameRequest)
      this.frameRequest = null
    }

    if (this.nextClientY !== null) {
      this.#applyDrag(this.nextClientY, this.nextTime)
      this.nextClientY = null
    }

    this.dragging = false
    if (this.panelTarget.hasPointerCapture(event.pointerId)) {
      this.panelTarget.releasePointerCapture(event.pointerId)
    }

    this.panelTarget.style.transition = ''
    if (this.hasOverlayTarget) this.overlayTarget.style.transition = ''
    this.#setWillChange(false)

    const shouldClose = this.translateY > this.height * CLOSE_THRESHOLD
      || this.velocity > VELOCITY_THRESHOLD

    shouldClose ? this.close() : this.#reset()
  }

  #applyDrag(clientY, time) {
    const delta = clientY - this.lastY
    if (Math.abs(delta) < 1) return

    const dt = time - this.lastTime
    if (dt > 0) this.velocity = (delta / dt) * 1000

    this.lastY = clientY
    this.lastTime = time

    this.translateY = Math.max(0, Math.min(this.height, this.translateY + delta))
    const progress = this.translateY / this.height

    this.panelTarget.style.transform = `translateY(${this.translateY}px)`
    if (this.hasOverlayTarget) this.overlayTarget.style.opacity = `${1 - progress}`
  }

  #reset() {
    this.translateY = 0
    this.velocity = 0
    this.panelTarget.style.transform = ''
    if (this.hasOverlayTarget) this.overlayTarget.style.opacity = ''
  }

  #setWillChange(enable) {
    this.panelTarget.style.willChange = enable ? 'transform' : ''
    if (this.hasOverlayTarget) this.overlayTarget.style.willChange = enable ? 'opacity' : ''
  }
}
